import cx from 'classnames';
import 'components/styles/DateTimePickerShared.scss';
import hoistNonReactStatic from 'hoist-non-react-statics';
import * as React from 'react';
import ReactDOM from 'react-dom';
import { MouseEventHandler } from 'types/typescript/eventHandlers';
import createChainedFunction from 'utils/createChainedFunction';

type Props = {
  autofocus?: boolean;
  onBlur?: () => void | null;
  onChange?: (d: Date) => void | null;
  onFocus?: () => void | null;
  placement?: Placement;
  className?: string;
};

type State = {
  opened: boolean;
  focused: boolean;
};

type Placement = 'top' | 'bottom';
type PlacementCSSClass = 'DateTimePicker--top' | 'DateTimePicker--bottom';

const placementToCSSClass: { [key in Placement]: PlacementCSSClass } = {
  top: 'DateTimePicker--top',
  bottom: 'DateTimePicker--bottom',
};

function withDateTimePickerPosition<T extends object>(
  Child: React.ComponentType<T>
): React.ComponentType<T & Props> {
  class DateTimePickerPosition extends React.Component<T & Props, State> {
    state: State = {
      opened: (this.props.autofocus as boolean) || false,
      focused: false,
    };

    displayName = `withDateTimePickerPosition(${Child.displayName})`;

    getPopupNodes = () => {
      if (this._picker.current) {
        const root = ReactDOM.findDOMNode(this._picker.current);
        if (isHTMLElement(root)) {
          return root.getElementsByClassName('rw-popup-container');
        } else {
          throw new Error('findDOMNode returned null');
        }
      }
      return [];
    };

    handleClick: MouseEventHandler = e => {
      let clickedPopup = false;
      [].forEach.call(this.getPopupNodes(), (n: Element) => {
        if (n.contains(e.target as Element)) {
          clickedPopup = true;
        }
      });

      if (!clickedPopup) {
        // if we didn't click the popup, toggle the popup
        this.setState({ opened: !this.state.opened });
      }
    };

    closePopup = () => {
      if (this.state.opened) {
        this.setState({ opened: false });
      }
    };

    handleFocus = () => {
      this.setState({ focused: true });
    };

    handleBlur = () => {
      this.setState({ focused: false });
      this.closePopup();
    };

    handleChange = () => {
      this.setState({ opened: false });
    };

    _picker = React.createRef<React.Component<T, {}>>();

    render() {
      const { onBlur, onChange, onFocus, className } = this.props;
      const { opened, focused } = this.state;

      // Since T.placement and Props.placement could both exist (in theory).
      // Typescript has trouble figuring out that (T & Props).placement is of type ?Placement
      // hence the cast to Placement | undefined
      const placement = (this.props.placement as Placement | undefined) || 'bottom';
      const placementClass = placementToCSSClass[placement];

      return (
        <Child
          {...this.props}
          placement={placement}
          className={cx('DateTimePicker', className, placementClass)}
          opened={opened}
          focused={focused}
          onBlur={createChainedFunction(this.handleBlur, onBlur)}
          onChange={createChainedFunction(this.handleChange, onChange)}
          onFocus={createChainedFunction(this.handleFocus, onFocus)}
          onPickerClick={this.handleClick}
          closePopup={this.closePopup}
          ref={this._picker}
        />
      );
    }
  }

  return hoistNonReactStatic(DateTimePickerPosition, Child);
}

export default withDateTimePickerPosition;

// TypeScript Window interface does not include some properties; HTMLElement
// is one of them, hence the cast to any
function isHTMLElement(item: Element | Text | null): item is HTMLElement {
  return item instanceof (window as any).HTMLElement;
}
